import { useApolloClient } from "@apollo/client";
import {
  DataGrid,
  DebouncedSearchInput,
  GridColDef,
  ListHeader,
  Modal,
  ModalOpenButton,
  PopoverOpenButton,
  Select,
} from "@msys/ui";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField as MuiTextField,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { green } from "@mui/material/colors";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { omit } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import {
  AddressInput as AddressInputGQLType,
  AttachmentSnapshot,
  namedOperations,
  TeamsSorting,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { FilterButton } from "../../commons/filters/FilterButton.js";
import { FilterChip } from "../../commons/filters/FilterChip.js";
import { FilterModal } from "../../commons/filters/FilterModal.js";
import { useFiltersAndPagination } from "../../commons/filters/useFiltersAndPagination.js";
import { SelectField } from "../../commons/form-fields/SelectField.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { AutocompletePopperSlot } from "../../commons/AutocompletePopperSlot.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { AddressDetails__AddressFragment } from "../../features/addresses/Addresses.generated.js";
import { AddressField } from "../../features/addresses/AddressField.js";
import { isAddressEqual } from "../../features/addresses/helpers.js";
import { UserAvatar } from "../../features/users/UserAvatar.js";
import {
  OrganisationTeamRowFragment,
  useOrganisationTeams_AddTeamMembersMutation,
  useOrganisationTeams_CreateTeamMutation,
  useOrganisationTeams_DeactivateTeamMutation,
  useOrganisationTeams_OrgMembersQuery,
  useOrganisationTeams_ActivateTeamMutation,
  useOrganisationTeams_RemoveTeamMembersMutation,
  useOrganisationTeams_TeamQuery,
  useOrganisationTeams_TeamsQuery,
  useOrganisationTeams_UpdateTeamMutation,
} from "./OrganisationTeams.generated.js";

interface Props {
  submenuItems: PageTopbarItem[];
}

const DEFAULT_SORTING: TeamsSorting[] = [{ column: "name", direction: "asc" }];

const REFETCH_LIST_QUERIES = [namedOperations.Query.OrganisationTeams_Teams];
const REFETCH_TEAM_QUERIES = [
  namedOperations.Query.OrganisationTeams_Teams,
  namedOperations.Query.OrganisationTeams_Team,
];

type TeamsFilterState = {
  type?: string;
  includeInactive?: boolean;
  searchTerm?: string;
};

export function OrganisationTeams({ submenuItems }: Props) {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);
  const client = useApolloClient();
  const [editingTeamId, setEditingTeamId] = React.useState<string | null>(null);
  const [managingMembersTeamId, setManagingMembersTeamId] = React.useState<
    string | null
  >(null);

  const {
    offset,
    limit,
    paginationModel,
    setPaginationModel,
    filters: { searchTerm, ...filters },
    setFilters,
    resetFilters,
    sorting,
  } = useFiltersAndPagination<TeamsSorting, TeamsFilterState>(
    {},
    DEFAULT_SORTING
  );

  const teamsQuery = useOrganisationTeams_TeamsQuery({
    client,
    variables: {
      offset,
      limit,
      filters: {
        type: filters.type ?? undefined,
        includeInactive: filters.includeInactive ?? false,
        search: searchTerm || undefined,
      },
      sorting,
    },
  });

  const teams = React.useMemo(() => {
    const result = teamsQuery.data?.teams;
    if (!result || result.__typename !== "TeamConnection") return [];
    return result.edges.map(e => e.node);
  }, [teamsQuery.data]);

  const totalCount = React.useMemo(() => {
    const result = teamsQuery.data?.teams;
    if (!result || result.__typename !== "TeamConnection") return 0;
    return result.totalCount;
  }, [teamsQuery.data]);

  const columns = React.useMemo(
    (): GridColDef<OrganisationTeamRowFragment>[] => [
      {
        field: "name",
        headerName: t("Name", { ns: "Global" }),
        flex: 2,
      },
      {
        field: "type",
        headerName: t("Type", { ns: "Global" }),
        flex: 1,
        renderCell: ({ row }) => (
          <Chip
            label={row.type}
            size="small"
            variant="outlined"
            color="secondary"
          />
        ),
      },
      {
        field: "memberCount",
        headerName: t("Members", { ns: "OrganisationProfile" }),
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <Button
            size="small"
            variant="text"
            startIcon={<GroupsIcon />}
            onClick={() => setManagingMembersTeamId(row.id)}
          >
            {row.memberCount}
          </Button>
        ),
      },
      {
        field: "address",
        headerName: t("Address", { ns: "Global" }),
        flex: 2,
        sortable: false,
        renderCell: ({ row }) => {
          if (!row.address) return null;
          const { city, streetLines1 } = row.address;
          return (
            <Typography variant="body2" noWrap>
              {[streetLines1, city].filter(Boolean).join(", ")}
            </Typography>
          );
        },
      },
      {
        field: "isActive",
        headerName: t("Status", { ns: "Global" }),
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <Chip
            label={
              row.isActive
                ? t("Active", { ns: "OrganisationProfile" })
                : t("Inactive", { ns: "OrganisationProfile" })
            }
            size="small"
            color={row.isActive ? "success" : "default"}
          />
        ),
      },
      {
        field: "actions",
        headerName: "",
        sortable: false,
        width: 0,
        align: "right",
        renderCell: ({ row }) => (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              justifyContent: "flex-end",
            }}
          >
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setEditingTeamId(row.id)}
            >
              {t("Edit", { ns: "Global" })}
            </Button>
          </Stack>
        ),
      },
    ],
    [t]
  );

  const typeLabel = (value: string) =>
    value === "sales-office"
      ? t("Sales office", { ns: "OrganisationProfile" })
      : value;
  const includeInactiveLabel = () =>
    t("Include inactive", { ns: "OrganisationProfile" });

  return (
    <Page
      title={t("Teams", { ns: "OrganisationProfile" })}
      submenuItems={submenuItems}
    >
      <PageContainer>
        <Stack spacing={2}>
          <ListHeader
            title={t("Teams", { ns: "OrganisationProfile" })}
            FilterButton={
              <ModalOpenButton
                Modal={TeamsFilterModal}
                modalProps={{
                  filters,
                  setFilters,
                  resetFilters,
                }}
              >
                <FilterButton />
              </ModalOpenButton>
            }
            ActiveFilterChips={
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: "center",
                }}
              >
                <FilterChip
                  label={t("Type", { ns: "Global" })}
                  value={filters.type}
                  getValueLabel={typeLabel}
                  onDelete={() => setFilters(s => ({ ...s, type: undefined }))}
                />
                <FilterChip
                  value={filters.includeInactive ? true : undefined}
                  getValueLabel={includeInactiveLabel}
                  onDelete={() =>
                    setFilters(s => ({ ...s, includeInactive: undefined }))
                  }
                />
              </Stack>
            }
            SearchField={
              <DebouncedSearchInput
                defaultValue={searchTerm ?? ""}
                onChangeSearchTerm={value =>
                  setFilters(s => ({
                    ...s,
                    searchTerm: value || undefined,
                  }))
                }
                placeholder={t("Search teams", {
                  ns: "OrganisationProfile",
                })}
              />
            }
            ActionButtons={
              <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_ORG">
                <ModalOpenButton
                  Modal={CreateTeamModal}
                  modalProps={{
                    refetchQueries: REFETCH_LIST_QUERIES,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    sx={{ flexShrink: 0 }}
                  >
                    {t("Create team", { ns: "OrganisationProfile" })}
                  </Button>
                </ModalOpenButton>
              </RestrictedByOrganisationPermissionWithDebug>
            }
          />
          <DataGrid
            columns={columns}
            rows={teams}
            rowCount={totalCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            loading={teamsQuery.loading}
            disableRowSelectionOnClick
            sx={{ "& .MuiDataGrid-row.MuiDataGrid-row": { cursor: "default" } }}
          />
        </Stack>
      </PageContainer>
      {editingTeamId && (
        <EditTeamModal
          teamId={editingTeamId}
          handleClose={() => setEditingTeamId(null)}
        />
      )}
      {managingMembersTeamId && (
        <ManageTeamMembersModal
          teamId={managingMembersTeamId}
          handleClose={() => setManagingMembersTeamId(null)}
        />
      )}
    </Page>
  );
}

const TEAM_TYPES = ["sales-office"] as const;
type TeamType = (typeof TEAM_TYPES)[number];

type TeamMember = {
  __typename: "User";
  id: string;
  firstname: string;
  familyname: string;
  fullname: string;
  email: string;
  avatar?: AttachmentSnapshot | null;
};

function useTeamTypeOptions(): { value: string; label: string }[] {
  const { t } = useTranslate(["OrganisationProfile"]);
  return React.useMemo(
    () =>
      TEAM_TYPES.map(value => ({
        value,
        label:
          value === "sales-office"
            ? t("Sales office", { ns: "OrganisationProfile" })
            : value,
      })),
    [t]
  );
}

function TeamsFilterModal({
  handleClose,
  filters,
  setFilters,
  resetFilters,
}: {
  handleClose: () => void;
  filters: TeamsFilterState;
  setFilters: React.Dispatch<React.SetStateAction<TeamsFilterState>>;
  resetFilters: () => void;
}) {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);
  const [filtersState, setFiltersState] = React.useState(filters);
  const typeOptions = useTeamTypeOptions();

  return (
    <FilterModal
      title={t("Teams filters", { ns: "OrganisationProfile" })}
      handleClose={handleClose}
      handleApply={() => {
        setFilters(s => ({ ...s, ...filtersState }));
      }}
      handleReset={() => resetFilters()}
      spacing={1}
    >
      <Select
        label={t("Type", { ns: "Global" })}
        options={typeOptions}
        value={filtersState.type ?? ""}
        onChange={value => {
          setFiltersState(s => ({
            ...s,
            type: value || undefined,
          }));
        }}
      />
      <FormControlLabel
        control={
          <Switch
            checked={filtersState.includeInactive ?? false}
            onChange={(_e, checked) => {
              setFiltersState(s => ({
                ...s,
                includeInactive: checked || undefined,
              }));
            }}
          />
        }
        label={t("Include inactive", { ns: "OrganisationProfile" })}
      />
    </FilterModal>
  );
}

interface TeamFormValues {
  name: string;
  type: string;
  metadataJson: string;
  address: AddressInputGQLType | null;
}

const createTeamInitialValues: TeamFormValues = {
  name: "",
  type: "sales-office",
  metadataJson: "",
  address: null,
};

function CreateTeamModal({
  handleClose,
  refetchQueries,
}: {
  handleClose: () => void;
  refetchQueries?: string[];
}) {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();
  const formId = React.useId();

  const [createTeam] = useOrganisationTeams_CreateTeamMutation({
    client,
    refetchQueries,
  });

  const validationSchema = useTeamValidationSchema();
  const typeOptions = useTeamTypeOptions();

  const handleSubmit = async (values: TeamFormValues) => {
    const parsed = parseMetadataJson(values.metadataJson);
    if (!parsed.ok) return;
    try {
      await createTeam({
        variables: {
          input: {
            name: values.name.trim(),
            type: values.type,
            metadata:
              Object.keys(parsed.value).length > 0 ? parsed.value : undefined,
            address: values.address,
            isActive: false,
          },
        },
      });
      enqueueSnackbar(t("Team created", { ns: "OrganisationProfile" }), {
        variant: "success",
      });
      handleClose();
    } catch {
      enqueueSnackbar(t("Error creating team", { ns: "OrganisationProfile" }), {
        variant: "error",
      });
    }
  };

  return (
    <Formik<TeamFormValues>
      initialValues={createTeamInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid, isSubmitting, dirty }) => (
        <Modal
          title={t("Create team", { ns: "OrganisationProfile" })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Cancel", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: { variant: "text", disabled: isSubmitting },
            },
            {
              label: t("Create", { ns: "Global" }),
              buttonProps: {
                type: "submit",
                form: formId,
                variant: "contained",
                disabled: !isValid || !dirty || isSubmitting,
                loading: isSubmitting,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack spacing={1}>
              <TextField
                name="name"
                label={t("Team name", { ns: "OrganisationProfile" })}
                fullWidth
                autoFocus
                required
              />
              <SelectField
                name="type"
                label={t("Type", { ns: "Global" })}
                options={typeOptions}
                fullWidth
                required
              />
              <MetadataJsonField
                name="metadataJson"
                label={t("Metadata", { ns: "OrganisationProfile" })}
                placeholder={METADATA_PLACEHOLDERS["sales-office"]}
              />
              <AddressField
                name="address"
                label={t("Address", { ns: "Global" })}
                allowCustom
              />
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
}

function EditTeamModal({
  teamId,
  handleClose,
}: {
  teamId: string;
  handleClose: () => void;
}) {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();
  const { hasOrganisationPermission } = useUserData();
  const canManage = hasOrganisationPermission("MANAGE_ORG");
  const formId = React.useId();

  const teamQuery = useOrganisationTeams_TeamQuery({
    client,
    variables: { id: teamId },
  });

  const teamData =
    teamQuery.data?.team.__typename === "Team" ? teamQuery.data.team : null;

  const initialMetadataJson = metadataToJson(teamData?.metadata);
  const initialAddress = toAddressInput(teamData?.address);
  const initialValues: TeamFormValues = {
    name: teamData?.name ?? "",
    type: teamData?.type ?? "sales-office",
    metadataJson: initialMetadataJson,
    address: initialAddress,
  };

  const [updateTeam] = useOrganisationTeams_UpdateTeamMutation({
    client,
    refetchQueries: REFETCH_LIST_QUERIES,
  });
  const [deactivateTeam, deactivateState] =
    useOrganisationTeams_DeactivateTeamMutation({
      client,
      refetchQueries: REFETCH_LIST_QUERIES,
    });
  const [activateTeam, activateState] =
    useOrganisationTeams_ActivateTeamMutation({
      client,
      refetchQueries: REFETCH_LIST_QUERIES,
    });

  const handleToggleActive = async () => {
    if (!teamData) return;
    try {
      if (teamData.isActive) {
        await deactivateTeam({ variables: { id: teamId } });
        enqueueSnackbar(t("Team deactivated", { ns: "OrganisationProfile" }), {
          variant: "success",
        });
      } else {
        await activateTeam({ variables: { id: teamId } });
        enqueueSnackbar(t("Team activated", { ns: "OrganisationProfile" }), {
          variant: "success",
        });
      }
      handleClose();
    } catch {
      enqueueSnackbar(t("Error updating team", { ns: "OrganisationProfile" }), {
        variant: "error",
      });
    }
  };

  const validationSchema = useTeamValidationSchema();
  const typeOptions = useTeamTypeOptions();

  const handleSave = async (values: TeamFormValues) => {
    if (!teamData) return;
    const parsed = parseMetadataJson(values.metadataJson);
    if (!parsed.ok) return;

    const nameChanged = values.name.trim() !== teamData.name;
    const metadataChanged =
      values.metadataJson.trim() !== initialMetadataJson.trim();
    const addressChanged = !isAddressEqual(values.address, initialAddress);

    if (!nameChanged && !metadataChanged && !addressChanged) {
      handleClose();
      return;
    }

    const updateInput: {
      id: string;
      name?: string;
      metadata?: Record<string, unknown>;
      address?: AddressInputGQLType | null;
    } = { id: teamId };
    if (nameChanged) updateInput.name = values.name.trim();
    if (metadataChanged) updateInput.metadata = parsed.value;
    if (addressChanged) updateInput.address = values.address;

    try {
      await updateTeam({ variables: { input: updateInput } });
      enqueueSnackbar(t("Team updated", { ns: "OrganisationProfile" }), {
        variant: "success",
      });
      handleClose();
    } catch {
      enqueueSnackbar(t("Error updating team", { ns: "OrganisationProfile" }), {
        variant: "error",
      });
    }
  };

  return (
    <Formik<TeamFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSave}
      enableReinitialize
    >
      {({ isValid, isSubmitting, dirty }) => {
        const toggleLoading = deactivateState.loading || activateState.loading;
        const busy = isSubmitting || toggleLoading;
        return (
          <Modal
            title={t("Edit team", { ns: "OrganisationProfile" })}
            handleClose={handleClose}
            actionButtons={
              canManage
                ? [
                    {
                      label: t("Cancel", { ns: "Global" }),
                      handleClick: handleClose,
                      buttonProps: { variant: "text", disabled: busy },
                    },
                    {
                      label: teamData?.isActive
                        ? t("Deactivate", { ns: "OrganisationProfile" })
                        : t("Activate", { ns: "OrganisationProfile" }),
                      handleClick: handleToggleActive,
                      buttonProps: {
                        variant: "outlined",
                        color: teamData?.isActive ? "warning" : "success",
                        disabled: !teamData || busy || dirty,
                        loading: toggleLoading,
                      },
                    },
                    {
                      label: t("Save", { ns: "Global" }),
                      buttonProps: {
                        type: "submit",
                        form: formId,
                        variant: "contained",
                        disabled: !dirty || !isValid || busy,
                        loading: isSubmitting,
                      },
                    },
                  ]
                : [
                    {
                      label: t("Close", { ns: "Global" }),
                      handleClick: handleClose,
                      buttonProps: { variant: "text" },
                    },
                  ]
            }
            isLoading={teamQuery.loading}
          >
            {teamData && (
              <Form id={formId}>
                <Stack spacing={1}>
                  <TextField
                    name="name"
                    label={t("Team name", { ns: "OrganisationProfile" })}
                    fullWidth
                    autoFocus={canManage}
                    required
                    disabled={!canManage}
                  />
                  <SelectField
                    name="type"
                    label={t("Type", { ns: "Global" })}
                    options={typeOptions}
                    fullWidth
                    disabled
                  />
                  <MetadataJsonField
                    name="metadataJson"
                    label={t("Metadata", { ns: "OrganisationProfile" })}
                    placeholder={METADATA_PLACEHOLDERS[teamData.type]}
                    disabled={!canManage}
                  />
                  <AddressField
                    name="address"
                    label={t("Address", { ns: "Global" })}
                    allowCustom={canManage}
                    disabled={!canManage}
                  />
                </Stack>
              </Form>
            )}
          </Modal>
        );
      }}
    </Formik>
  );
}

function ManageTeamMembersModal({
  teamId,
  handleClose,
}: {
  teamId: string;
  handleClose: () => void;
}) {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();
  const { hasOrganisationPermission } = useUserData();
  const canManage = hasOrganisationPermission("MANAGE_ORG");

  const teamQuery = useOrganisationTeams_TeamQuery({
    client,
    variables: { id: teamId },
  });

  const teamData =
    teamQuery.data?.team.__typename === "Team" ? teamQuery.data.team : null;

  const [pendingAdds, setPendingAdds] = React.useState<string[]>([]);
  const [pendingRemovals, setPendingRemovals] = React.useState<Set<string>>(
    () => new Set()
  );

  const [addMembers, addState] = useOrganisationTeams_AddTeamMembersMutation({
    client,
    refetchQueries: REFETCH_TEAM_QUERIES,
  });
  const [removeMembers, removeState] =
    useOrganisationTeams_RemoveTeamMembersMutation({
      client,
      refetchQueries: REFETCH_TEAM_QUERIES,
    });
  const saving = addState.loading || removeState.loading;

  const hasChanges = pendingAdds.length > 0 || pendingRemovals.size > 0;

  const handleSave = async () => {
    if (!hasChanges) {
      handleClose();
      return;
    }
    try {
      const promises: Promise<unknown>[] = [];
      if (pendingAdds.length > 0) {
        promises.push(
          addMembers({
            variables: { input: { teamId, userIds: pendingAdds } },
          })
        );
      }
      if (pendingRemovals.size > 0) {
        promises.push(
          removeMembers({
            variables: { input: { teamId, userIds: [...pendingRemovals] } },
          })
        );
      }
      await Promise.all(promises);
      enqueueSnackbar(t("Memberships updated", { ns: "OrganisationProfile" }), {
        variant: "success",
      });
      handleClose();
    } catch {
      enqueueSnackbar(t("Error updating team", { ns: "OrganisationProfile" }), {
        variant: "error",
      });
    }
  };

  return (
    <Modal
      title={t("Edit members", { ns: "OrganisationProfile" })}
      handleClose={handleClose}
      actionButtons={
        canManage
          ? [
              {
                label: t("Cancel", { ns: "Global" }),
                handleClick: handleClose,
                buttonProps: { variant: "text", disabled: saving },
              },
              {
                label: t("Save", { ns: "Global" }),
                handleClick: handleSave,
                buttonProps: {
                  variant: "contained",
                  disabled: !hasChanges || saving,
                  loading: saving,
                },
              },
            ]
          : [
              {
                label: t("Close", { ns: "Global" }),
                handleClick: handleClose,
                buttonProps: { variant: "text" },
              },
            ]
      }
      isLoading={teamQuery.loading}
    >
      {teamData && (
        <TeamMembersSection
          members={teamData.members}
          pendingAdds={pendingAdds}
          setPendingAdds={setPendingAdds}
          pendingRemovals={pendingRemovals}
          setPendingRemovals={setPendingRemovals}
        />
      )}
    </Modal>
  );
}

function toAddressInput(
  address: AddressDetails__AddressFragment | null | undefined
): AddressInputGQLType | null {
  if (!address) return null;
  return {
    ...omit(address, "__typename", "id"),
    location: address.location ? omit(address.location, "__typename") : null,
  };
}

function metadataToJson(metadata: unknown): string {
  const meta = (metadata ?? {}) as Record<string, unknown>;
  return Object.keys(meta).length > 0 ? JSON.stringify(meta, null, 2) : "";
}

function useTeamValidationSchema() {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);

  return React.useMemo(() => {
    const metadataYupSchema = Yup.string().test(
      "valid-json-object",
      function (value) {
        const trimmed = (value ?? "").trim();
        if (!trimmed) return true;
        try {
          const parsed = JSON.parse(trimmed);
          if (
            parsed === null ||
            typeof parsed !== "object" ||
            Array.isArray(parsed)
          ) {
            throw new Error("Not an object");
          }
          return true;
        } catch {
          return this.createError({
            message: t("Invalid JSON object", { ns: "Global" }),
          });
        }
      }
    );

    const addressYupSchema = Yup.object()
      .shape({
        streetLines1: Yup.string()
          .label(t("Street", { ns: "Global" }))
          .required(),
        postalCode: Yup.string()
          .label(t("Postal code", { ns: "Global" }))
          .required(),
        city: Yup.string()
          .label(t("City", { ns: "Global" }))
          .required(),
        countryCode: Yup.string()
          .label(t("Country", { ns: "Global" }))
          .required(),
      })
      .nullable()
      .default(null);

    return Yup.object({
      name: Yup.string()
        .trim()
        .label(t("Team name", { ns: "OrganisationProfile" }))
        .required(),
      type: Yup.string()
        .label(t("Type", { ns: "Global" }))
        .required(),
      metadataJson: metadataYupSchema,
      address: addressYupSchema,
    });
  }, [t]);
}

function TeamMembersSection({
  members,
  pendingAdds,
  setPendingAdds,
  pendingRemovals,
  setPendingRemovals,
}: {
  members: TeamMember[];
  pendingAdds: string[];
  setPendingAdds: React.Dispatch<React.SetStateAction<string[]>>;
  pendingRemovals: Set<string>;
  setPendingRemovals: React.Dispatch<React.SetStateAction<Set<string>>>;
}) {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);
  const client = useApolloClient();

  const orgMembersQuery = useOrganisationTeams_OrgMembersQuery({
    client,
    variables: {
      limit: 500,
      sorting: [{ column: "firstname", direction: "asc" }],
    },
  });

  const orgMembers = React.useMemo(() => {
    const result = orgMembersQuery.data?.organisationMemberships;
    if (!result || result.__typename !== "OrganisationMembershipsConnection")
      return [];
    return result.edges.map(e => e.node);
  }, [orgMembersQuery.data]);

  const { availableMembers, pendingAddMembers } = React.useMemo(() => {
    const existingMemberIds = new Set(members.map(m => m.id));
    const pendingAddsSet = new Set(pendingAdds);
    return {
      availableMembers: orgMembers.filter(
        m => !existingMemberIds.has(m.id) && !pendingAddsSet.has(m.id)
      ),
      pendingAddMembers: orgMembers.filter(m => pendingAddsSet.has(m.id)),
    };
  }, [members, pendingAdds, orgMembers]);

  const visibleCount =
    members.length - pendingRemovals.size + pendingAdds.length;

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle2">
          {t("Members", { ns: "OrganisationProfile" })} ({visibleCount})
        </Typography>
        <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_ORG">
          <PopoverOpenButton
            content={
              <AddMemberPopoverContent
                availableMembers={availableMembers}
                onSelect={userId => setPendingAdds(prev => [...prev, userId])}
              />
            }
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Button size="small" startIcon={<PersonAddIcon />}>
              {t("Add", { ns: "Global" })}
            </Button>
          </PopoverOpenButton>
        </RestrictedByOrganisationPermissionWithDebug>
      </Stack>
      {members.length === 0 && pendingAdds.length === 0 ? (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            py: 2,
          }}
        >
          {t("No members yet", { ns: "OrganisationProfile" })}
        </Typography>
      ) : (
        <Box>
          <List dense disablePadding sx={{ mx: -1.5 }}>
            {members.map(member => {
              const isRemoved = pendingRemovals.has(member.id);
              return (
                <ListItem
                  key={member.id}
                  secondaryAction={
                    <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_ORG">
                      {isRemoved ? (
                        <Button
                          onClick={() =>
                            setPendingRemovals(prev => {
                              const next = new Set(prev);
                              next.delete(member.id);
                              return next;
                            })
                          }
                        >
                          {t("Undo", { ns: "Global" })}
                        </Button>
                      ) : (
                        <IconButton
                          onClick={() =>
                            setPendingRemovals(prev =>
                              new Set(prev).add(member.id)
                            )
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </RestrictedByOrganisationPermissionWithDebug>
                  }
                  sx={
                    isRemoved
                      ? { opacity: 0.5, textDecoration: "line-through" }
                      : undefined
                  }
                >
                  <ListItemAvatar>
                    <UserAvatar userAvatar={member} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={member.fullname}
                    secondary={member.email}
                  />
                </ListItem>
              );
            })}
            {pendingAddMembers.map(member => (
              <ListItem
                key={member.id}
                secondaryAction={
                  <IconButton
                    onClick={() =>
                      setPendingAdds(prev =>
                        prev.filter(id => id !== member.id)
                      )
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
                sx={{
                  backgroundColor: green[100],
                }}
              >
                <ListItemAvatar>
                  <UserAvatar userAvatar={member} />
                </ListItemAvatar>
                <ListItemText
                  primary={member.fullname}
                  secondary={member.email}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Stack>
  );
}

function AddMemberPopoverContent({
  availableMembers,
  onSelect,
}: {
  availableMembers: TeamMember[];
  onSelect: (userId: string) => void;
}) {
  const { t } = useTranslate(["OrganisationProfile"]);

  return (
    <Box sx={{ width: 320 }}>
      <Autocomplete
        open
        size="small"
        options={availableMembers}
        getOptionLabel={option => `${option.fullname} (${option.email})`}
        value={null}
        onChange={(_e, value) => {
          if (value) {
            onSelect(value.id);
          }
        }}
        renderInput={params => (
          <MuiTextField
            {...params}
            label={t("Select member", {
              ns: "OrganisationProfile",
            })}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                py: 0.5,
              }}
            >
              <UserAvatar userAvatar={option} size="xs" />
              <ListItemText
                primary={option.fullname}
                slotProps={{
                  primary: { variant: "body2" },
                  secondary: { variant: "caption" },
                }}
              />
            </Stack>
          </li>
        )}
        slots={{
          popper: AutocompletePopperSlot,
        }}
        disablePortal
        blurOnSelect
        clearOnBlur
      />
    </Box>
  );
}

const METADATA_PLACEHOLDERS: Record<string, string> = {
  "sales-office": `{
  "externalId": "SO-001",
  "sapRegion": "EU",
  "sapCostCentre": "1000"
}`,
};

function parseMetadataJson(
  value: string
): { ok: true; value: Record<string, unknown> } | { ok: false; error: string } {
  const trimmed = value.trim();
  if (!trimmed) return { ok: true, value: {} };
  try {
    const parsed = JSON.parse(trimmed);
    if (
      parsed === null ||
      typeof parsed !== "object" ||
      Array.isArray(parsed)
    ) {
      return { ok: false, error: "Metadata must be a JSON object" };
    }
    return { ok: true, value: parsed as Record<string, unknown> };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : "Invalid JSON",
    };
  }
}

function MetadataJsonField({
  name,
  label,
  placeholder,
  disabled,
}: {
  name: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <TextField
      name={name}
      label={label}
      placeholder={placeholder}
      fullWidth
      multiline
      minRows={4}
      disabled={disabled}
      slotProps={{
        htmlInput: { style: { fontFamily: "monospace" } },
      }}
    />
  );
}
